import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import OptionCard from "./OptionCard";
import DemoProgressBar from "./DemoProgressBar";
import DemoSlotPicker from "./DemoSlotPicker";
import { DEMO_MAX_SLOT_SELECTIONS, serializeDemoSlots } from "./demoBookingRules";
import { fetchDemoBookingAvailability } from "./parseDemoBookingFromPublic";
import StepContainer from "./StepContainer";
import {
  TOTAL_DEMO_STEPS,
  DEMO_ROLE_OPTIONS,
  DEMO_TEACHING_AREA_OPTIONS,
  DEMO_CLASS_SIZE_OPTIONS,
  DEMO_MAIN_INTEREST_OPTIONS,
  DEMO_MAIN_GOAL_OPTIONS,
  DEMO_PILOT_OPTIONS,
} from "./constants";
import { CallAPI } from "apiManager/ApiCall";
import { demoRequestURL } from "apiManager/APIConfig";

const initialForm = () => ({
  role: "Professor",
  teachingArea: "Histology",
  classSize: null,
  mainInterest: null,
  mainGoal: null,
  demoSlots: [],
  email: "",
  phone: "",
  pilot: null,
});

function isValidEmail(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

async function postDemoRequest(payload) {
  const webhook = process.env.REACT_APP_DEMO_REQUEST_WEBHOOK;
  if (webhook) {
    try {
      await fetch(webhook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "demo_request", ...payload }),
      });
    } catch (e) {
      console.warn("REACT_APP_DEMO_REQUEST_WEBHOOK failed", e);
    }
  }

  const slotPayload = serializeDemoSlots(
    payload.demoSlots || [],
    payload.timezoneLabel,
  );
  const body = {
    role: payload.role,
    teachingArea: payload.teachingArea,
    classSize: payload.classSize,
    mainInterest: payload.mainInterest,
    mainGoal: payload.mainGoal,
    ...slotPayload,
    email: payload.email,
    phone: payload.phone || "",
    pilot: payload.pilot,
  };

  const res = await CallAPI({ method: "POST", url: demoRequestURL, body });
  const st = typeof res?.status === "number" ? res.status : null;
  if (st != null && st >= 400) {
    throw new Error(res?.message || "Could not submit demo request.");
  }
}

export default function RequestDemoDialog({ open, onClose, defaultEmail = "" }) {
  const theme = useTheme();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [availableDemoSlots, setAvailableDemoSlots] = useState([]);
  const [demoSlotsLoading, setDemoSlotsLoading] = useState(false);
  const [demoFullyBooked, setDemoFullyBooked] = useState(false);
  const [demoTimezoneLabel, setDemoTimezoneLabel] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm({ ...initialForm(), email: defaultEmail || "" });
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open, defaultEmail]);

  useEffect(() => {
    if (step !== 6) return undefined;
    let cancelled = false;
    setDemoSlotsLoading(true);
    setDemoFullyBooked(false);
    void (async () => {
      const { slots, fullyBooked, timezoneLabel } = await fetchDemoBookingAvailability();
      if (cancelled) return;
      setAvailableDemoSlots(slots);
      setDemoTimezoneLabel(timezoneLabel);
      setDemoFullyBooked(fullyBooked);
      setDemoSlotsLoading(false);
      if (fullyBooked) {
        setForm((f) => ({ ...f, demoSlots: [] }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step]);

  useEffect(() => {
    if (step === 6 && !demoFullyBooked && form.demoSlots.length === DEMO_MAX_SLOT_SELECTIONS) {
      const t = setTimeout(() => setStep(7), 220);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step, form.demoSlots.length, demoFullyBooked]);

  const confirmQuit = useCallback(() => {
    return window.confirm(
      "Your progress will be lost. Are you sure you want to leave the demo request?",
    );
  }, []);

  const goBack = useCallback(() => {
    if (success) {
      onClose();
      return;
    }
    if (step > 1) {
      setStep((s) => s - 1);
      return;
    }
    if (confirmQuit()) {
      onClose();
    }
  }, [confirmQuit, onClose, step, success]);

  const handleClose = useCallback(() => {
    if (success) {
      onClose();
      return;
    }
    if (confirmQuit()) {
      onClose();
    }
  }, [confirmQuit, onClose, success]);

  const pick = (key, value, nextStep) => {
    setForm((f) => ({ ...f, [key]: value }));
    setStep(nextStep);
  };

  const submitWithPilot = async (pilotValue) => {
    const payload = { ...form, pilot: pilotValue, timezoneLabel: demoTimezoneLabel };
    setSubmitting(true);
    try {
      await postDemoRequest(payload);
      setSuccess(true);
      setStep(10);
    } catch (e) {
      console.error("Demo request submit failed", e);
      window.alert(e?.message || "Could not submit your demo request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (success || step === 10) {
      return (
        <Fade in timeout={400}>
          <Box sx={{ textAlign: "center", py: 3, px: 1 }}>
            <MDTypography variant="h5" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
              Thank you
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary" sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.7 }}>
              Your demo request has been received. We’ll contact you shortly.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="primary"
              onClick={onClose}
              sx={{ mt: 3, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 4 }}
            >
              Close
            </MDButton>
          </Box>
        </Fade>
      );
    }

    switch (step) {
      case 1:
        return (
          <StepContainer title="What’s your role?" hint="Takes 20 seconds • Tailored to your course">
            {DEMO_ROLE_OPTIONS.map((label) => (
              <OptionCard
                key={label}
                selected={form.role === label}
                label={label}
                onClick={() => pick("role", label, 2)}
              />
            ))}
            <MDButton
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setStep(2)}
              sx={{ mt: 0.5, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 1.1 }}
            >
              Continue with “{form.role}”
            </MDButton>
          </StepContainer>
        );
      case 2:
        return (
          <StepContainer title="Teaching area">
            {DEMO_TEACHING_AREA_OPTIONS.map((label) => (
              <OptionCard
                key={label}
                selected={form.teachingArea === label}
                label={label}
                onClick={() => pick("teachingArea", label, 3)}
              />
            ))}
            <MDButton
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setStep(3)}
              sx={{ mt: 0.5, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 1.1 }}
            >
              Continue with “{form.teachingArea}”
            </MDButton>
          </StepContainer>
        );
      case 3:
        return (
          <StepContainer title="Typical class size">
            {DEMO_CLASS_SIZE_OPTIONS.map((label) => (
              <OptionCard key={label} selected={form.classSize === label} label={label} onClick={() => pick("classSize", label, 4)} />
            ))}
          </StepContainer>
        );
      case 4:
        return (
          <StepContainer title="Main interest">
            {DEMO_MAIN_INTEREST_OPTIONS.map((label) => (
              <OptionCard
                key={label}
                selected={form.mainInterest === label}
                label={label}
                onClick={() => pick("mainInterest", label, 5)}
              />
            ))}
          </StepContainer>
        );
      case 5:
        return (
          <StepContainer title="Main goal / pain point">
            {DEMO_MAIN_GOAL_OPTIONS.map((label) => (
              <OptionCard key={label} selected={form.mainGoal === label} label={label} onClick={() => pick("mainGoal", label, 6)} />
            ))}
          </StepContainer>
        );
      case 6:
        return (
          <StepContainer
            title="Pick 2 preferred demo times"
            hint={
              demoFullyBooked
                ? ""
                : form.demoSlots.length === 0
                  ? "Choose 2 slots below. Dates are generated automatically for the next 3 weeks."
                : form.demoSlots.length === 1
                  ? "Please choose one more time slot."
                  : "Great — continuing…"
            }
          >
            {!demoSlotsLoading && !demoFullyBooked && demoTimezoneLabel ? (
              <MDTypography variant="caption" fontWeight={700} color="primary" sx={{ display: "block", mb: 1 }}>
                {demoTimezoneLabel}
              </MDTypography>
            ) : null}
            {!demoSlotsLoading && !demoFullyBooked ? (
              <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, lineHeight: 1.6 }}>
                If these times do not work for you, we can reschedule via email after you submit.
              </MDTypography>
            ) : null}
            {demoSlotsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : demoFullyBooked ? (
              <MDTypography variant="body1" color="text.primary" sx={{ py: 2, lineHeight: 1.7, textAlign: "center" }}>
                We are fully booked for this month. Please check back later or contact us.
              </MDTypography>
            ) : (
              <DemoSlotPicker
                availableSlots={availableDemoSlots}
                selectedSlots={form.demoSlots}
                onChange={(demoSlots) => setForm((f) => ({ ...f, demoSlots }))}
              />
            )}
          </StepContainer>
        );
      case 7:
        return (
          <StepContainer title="Contact details">
            <TextField
              fullWidth
              required
              label="Work email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Phone or WhatsApp (optional)"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <MDButton
              variant="gradient"
              color="primary"
              fullWidth
              disabled={!isValidEmail(form.email)}
              onClick={() => setStep(8)}
              sx={{ mt: 1, textTransform: "none", fontWeight: 700, borderRadius: 2, py: 1.25 }}
            >
              Continue
            </MDButton>
          </StepContainer>
        );
      case 8:
        return (
          <StepContainer title="Would you like a free pilot for your students?">
            {DEMO_PILOT_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.value}
                selected={form.pilot === opt.value}
                label={opt.label}
                onClick={() => submitWithPilot(opt.value)}
                disabled={submitting}
              />
            ))}
          </StepContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <IconButton size="small" onClick={goBack} aria-label="Back" sx={{ color: "text.secondary" }}>
            <ArrowBackIcon />
          </IconButton>
          <MDTypography variant="button" fontWeight={700} color="primary" sx={{ letterSpacing: 0.5 }}>
            Request a demo
          </MDTypography>
          <IconButton size="small" onClick={handleClose} aria-label="Close" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </MDBox>

        {!success && step !== 10 && <DemoProgressBar current={step} total={TOTAL_DEMO_STEPS} />}

        <Fade in key={step} timeout={280}>
          <Box sx={{ position: "relative", minHeight: success ? 120 : 280 }}>
            {submitting && step === 9 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              renderBody()
            )}
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  );
}

RequestDemoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultEmail: PropTypes.string,
};

RequestDemoDialog.defaultProps = {
  defaultEmail: "",
};
